import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const fmtVES = (n) => `VES ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Cobros de Débito Inmediato que Banco Plaza confirmó con 0000 y nunca acreditó
// en la cuenta cobradora. El verificador ya revierte el asiento del ledger; esta
// tarjeta los hace visibles (antes solo quedaban en el log y en un correo).
export default function DIUnsettledAlert() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/di-unsettled`, { headers: headers(), params: { days: 7 } });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = async () => {
    const res = await axios.get(`${API}/di-unsettled/export`, {
      headers: headers(), params: { days: 7 }, responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cobros_no_acreditados.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return null;
  if (!data || data.total_count === 0) return null;

  return (
    <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-5" data-testid="di-unsettled-alert">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-bold text-red-900">Cobros confirmados por el banco y no acreditados</h3>
            <p className="text-xs text-red-800 mt-1 max-w-2xl">
              Banco Plaza respondió <span className="font-mono">0000</span> al cobro pero el crédito
              nunca entró a la cuenta cobradora. El asiento del ledger ya fue revertido
              automáticamente; estos casos se reclaman al banco.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-xs px-3 py-1.5 border border-red-300 rounded-lg hover:bg-red-100 flex items-center gap-1" data-testid="di-unsettled-refresh">
            <RefreshCw className="w-3 h-3" /> Refrescar
          </button>
          <button onClick={download} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1" data-testid="di-unsettled-export">
            <Download className="w-3 h-3" /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="bg-white rounded-lg border border-red-200 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-red-700">Hoy</p>
          <p className="text-xl font-bold text-red-900" data-testid="di-unsettled-today-count">{data.today_count}</p>
          <p className="text-xs font-mono text-red-800">{fmtVES(data.today_amount)}</p>
        </div>
        <div className="bg-white rounded-lg border border-red-200 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-red-700">Últimos 7 días</p>
          <p className="text-xl font-bold text-red-900" data-testid="di-unsettled-total-count">{data.total_count}</p>
          <p className="text-xs font-mono text-red-800" data-testid="di-unsettled-total-amount">{fmtVES(data.total_amount)}</p>
        </div>
        <div className="bg-white rounded-lg border border-red-200 px-3 py-2 col-span-2">
          <p className="text-[10px] uppercase tracking-wider text-red-700 mb-1">Por comercio</p>
          {data.by_merchant.map((m) => (
            <div key={m.merchant_id} className="flex items-center justify-between text-xs">
              <span className="truncate">{m.company_name || m.email}</span>
              <span className="font-mono font-semibold ml-2">{m.count} · {fmtVES(m.total_amount)}</span>
            </div>
          ))}
        </div>
      </div>

      <button onClick={() => setExpanded(!expanded)} className="mt-3 text-xs text-red-800 underline" data-testid="di-unsettled-toggle">
        {expanded ? 'Ocultar detalle' : `Ver los ${data.total_count} cobros`}
      </button>

      {expanded && (
        <div className="mt-3 bg-white rounded-lg border border-red-200 overflow-x-auto" data-testid="di-unsettled-table">
          <table className="w-full text-xs">
            <thead className="bg-red-100 text-red-900">
              <tr>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Orden</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2 text-left">Endtoend</th>
                <th className="px-3 py-2 text-left">Pagador</th>
                <th className="px-3 py-2 text-left">Banco</th>
                <th className="px-3 py-2 text-right">Chequeos</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((it) => (
                <tr key={it.order_id}>
                  <td className="px-3 py-1.5 whitespace-nowrap">{new Date(it.charged_at).toLocaleString('es-VE')}</td>
                  <td className="px-3 py-1.5 font-mono">{it.order_code}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtVES(it.amount)}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{it.endtoend}</td>
                  <td className="px-3 py-1.5">{it.payer_name}<span className="block text-[10px] text-gray-500 font-mono">{it.payer_document} · {it.payer_phone}</span></td>
                  <td className="px-3 py-1.5 font-mono">{it.payer_bank}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{it.bank_ok_checks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
